'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { FormRenderer } from '@jlg-website/form/renderer/client';
import type {
  Answers,
  FormConfig,
  FormInput,
  FormLocale,
  FormSubmitPayload,
} from '@jlg-website/shared';
import { useLeadForm } from '@/hooks/use-lead-form';
import { buildAnswersQuery } from '@/components/pages/form/answer-query';
import { buildRenderCompletion } from '@/components/pages/form/completion';
import type { FormVariant } from '@/components/pages/form/type';

type EmbeddedFormClientProps = {
  schema: FormInput;
  /** Drives the host's `data-form-variant` scope; the host owns the matching stylesheet. */
  variant: FormVariant;
  config?: FormConfig;
  locale?: FormLocale;
  /** Advance button on a non-final step (multi-step teaser). */
  nextLabel: string;
  /** Submit button on the last step (a single-step form submits inline). */
  finishLabel: string;
};

/**
 * The form as embedded in a host surface (the mass-tort hero card, the footer card). Renders the
 * *same* engine — and fires the *same* lead/marketing events (via `useLeadForm`) — as the page's
 * full form, but with its own flow: `autoNext` is on, and a multi-step form is only ever answered
 * up to its first step here. Selecting an answer auto-advances, which hands the visitor over to
 * `/forms/<page-slug>` with the first step's answers in the query, where the full form pre-selects
 * them and carries on. A single-step form is the last step, so it does not auto-advance: the
 * visitor clicks to submit, inline, exactly like the default client.
 *
 * The hand-off target is derived from the current path, so it only resolves on a route that has a
 * `/forms/<slug>` counterpart — which is exactly where a page-linked multi-step form is rendered.
 */
export default function EmbeddedFormClient({
  schema,
  variant,
  config,
  locale,
  nextLabel,
  finishLabel,
}: EmbeddedFormClientProps) {
  const router = useRouter();
  const pathname = usePathname();
  const {
    config: configWithValidators,
    handleSubmit,
    handleStepNext,
    handleReachLastStep,
  } = useLeadForm(schema, config);

  // The hand-off target: the full form for this same page. The query string (the first step's
  // answers) is only known at hand-off, but prefetch is keyed by route segments, not the query — so
  // warming the pathname now downloads the form's chunks ahead of the redirect regardless of which
  // answers get appended later. Only a multi-step form ever hands off, so a single-step one skips
  // the prefetch entirely.
  const isSingleStep = schema.form_steps.length === 1;
  const formRoute = `/forms${pathname}`;
  useEffect(() => {
    if (isSingleStep) return;
    router.prefetch(formRoute);
  }, [router, formRoute, isSingleStep]);

  // Wraps the live form so its rendered height can be captured the instant before hand-off, letting
  // the placeholder hold the card at exactly the size it had on step one (no squashing).
  const contentRef = useRef<HTMLDivElement>(null);
  const [handoffHeight, setHandoffHeight] = useState<number | null>(null);

  // Once the visitor advances past step one, the card is swapped for a spinner placeholder while the
  // browser navigates to the full form — so the host can no longer be answered.
  const [isHandingOff, setIsHandingOff] = useState(false);

  // A single-step form submits inline (no hand-off), so it shows the completion screen here. Its
  // "Close" button remounts the renderer via this key, clearing answers + status for a fresh form.
  const [resetToken, setResetToken] = useState(0);
  const handleClose = useCallback(() => setResetToken((token) => token + 1), []);

  // Fire the shared progress event, then (multi-step only) hand off. Guarded to the first step; in
  // practice we redirect before the engine reaches a second one.
  const onStepNext = useCallback(
    (payload: FormSubmitPayload): void => {
      handleStepNext(payload);

      if (payload.current_step !== 1) return;

      const firstStepAnswers: Answers = {};
      Object.assign(firstStepAnswers, payload.answers);

      setHandoffHeight(contentRef.current?.offsetHeight ?? null);
      setIsHandingOff(true);
      router.push(`/forms${pathname}${buildAnswersQuery(firstStepAnswers)}`);
    },
    [handleStepNext, router, pathname]
  );

  // A `redirect` fires `onRedirect` instead of `onStepNext`; emit the same progress event so the
  // hand-off is still reported. No navigation here — the engine drives the redirect itself.
  const onRedirect = useCallback(
    (payload: FormSubmitPayload): void => {
      handleStepNext(payload);
    },
    [handleStepNext]
  );

  // Triggers the event for the first step on Form load
  useEffect(() => {
    handleStepNext({
      answers: {},
      is_qualified: false,
      current_step: 1,
      is_finished: false,
    } as FormSubmitPayload);
  }, [handleStepNext]);

  if (isHandingOff) {
    return (
      <div
        data-form-handoff=""
        aria-busy="true"
        style={handoffHeight ? { minHeight: handoffHeight } : undefined}
      >
        <HandoffSpinner />
      </div>
    );
  }

  return (
    <div ref={contentRef}>
      <FormRenderer
        key={resetToken}
        schema={schema}
        config={{ ...configWithValidators }}
        variant={variant}
        locale={locale}
        autoNext
        labels={{ next: nextLabel, finish: finishLabel }}
        onSubmit={handleSubmit}
        onStepNext={onStepNext}
        onRedirect={onRedirect}
        onReachLastStep={handleReachLastStep}
        enableNextOnValidate={false}
        renderCompletion={buildRenderCompletion(configWithValidators.completionMessage)}
        completionActionLabel={isSingleStep ? 'Close' : undefined}
        onCompletionAction={isSingleStep ? handleClose : undefined}
      />
    </div>
  );
}

/**
 * Loading spinner shown in the hand-off placeholder: a single 3/4 SVG arc spun by CSS
 * (`embedded/structure.css`). Kept inline so an embedded form owns its own feedback UI without
 * pulling in an icon dependency.
 */
function HandoffSpinner() {
  return (
    <svg data-form-handoff-spinner="" viewBox="0 0 50 50" role="status" aria-label="Loading">
      <circle cx="25" cy="25" r="20" />
    </svg>
  );
}
